import React from "react";
import {
  Check,
  ChevronRight,
  MessageCircle,
  Pencil,
  Plus,
  ShieldCheck,
} from "lucide-react";
import EmptyCard from "../common/EmptyCard";
import LoadingCard from "../common/LoadingCard";

function WhatsAppAccountSection({
  accounts,
  selectedAccountId,
  loading,
  workspaceId,
  onSelect,
  onAdd,
  onEdit,
}) {
  return (
    <div className="h-full rounded-[22px] border border-[#d8e7ed] bg-white p-4 shadow-[0_14px_40px_rgba(15,43,66,0.06)] sm:p-5 dark:border-dark-border dark:bg-dark-card">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between gap-3 sm:mb-5">
        <div className="flex min-w-0 items-center gap-2.5 sm:gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#cfe4ed] bg-[#f3f9fc] text-[#47758f] sm:h-10 sm:w-10 dark:border-dark-border dark:bg-dark-surface dark:text-dark-text-secondary">
            <MessageCircle size={16} strokeWidth={1.8} className="sm:hidden" />

            <MessageCircle
              size={18}
              strokeWidth={1.8}
              className="hidden sm:block"
            />
          </div>

          <div className="min-w-0">
            <h3 className="truncate text-[11px] font-semibold text-text-primary sm:text-xs dark:text-dark-text-primary">
              WhatsApp Account
            </h3>

            <p className="mt-0.5 truncate text-[9px] text-text-muted sm:text-[10px] dark:text-dark-text-muted">
              Business account
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <ShieldCheck
            size={14}
            strokeWidth={1.8}
            className="text-primary sm:h-4 sm:w-4 dark:text-dark-primary"
          />

          {onAdd && (
            <button
              type="button"
              onClick={onAdd}
              disabled={!workspaceId || !onAdd}
              aria-label="Add WhatsApp account"
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#d8e7ed] text-[#607787] transition-all hover:border-[#9bcbe2] hover:bg-[#edf8fd] hover:text-[#147db5] disabled:cursor-not-allowed disabled:opacity-40 dark:border-dark-border dark:text-dark-text-secondary dark:hover:border-dark-primary/45 dark:hover:bg-dark-soft-blue dark:hover:text-dark-primary"
            >
              <Plus size={13} strokeWidth={1.8} />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <LoadingCard small />
      ) : accounts.length === 0 ? (
        <>
          <EmptyCard text="No WhatsApp accounts found for this workspace." />

          {onAdd && (
            <button
              type="button"
              onClick={onAdd}
              disabled={!workspaceId || !onAdd}
              className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-theme-200 bg-theme-50 px-3 py-2.5 text-[9px] font-semibold text-primary transition-all hover:border-theme-300 hover:bg-theme-100 disabled:cursor-not-allowed disabled:opacity-40 sm:gap-2 sm:text-[10px] dark:border-dark-border dark:bg-dark-surface dark:text-dark-primary dark:hover:border-dark-primary/40 dark:hover:bg-dark-soft-blue"
            >
              <Plus size={12} strokeWidth={1.8} />
              Connect WhatsApp account
            </button>
          )}
        </>
      ) : (
        <div className="space-y-2">
          {accounts.map((account) => {
            const selected = String(account.id) === String(selectedAccountId);

            return (
              <div
                key={account.id}
                className={[
                  "flex w-full min-w-0 items-center gap-2 rounded-xl border p-2.5 text-left transition-all duration-200 sm:gap-2.5 sm:p-3",
                  selected
                    ? "border-[#9bcbe2] bg-[#f1f9fd] shadow-sm dark:border-dark-primary/50 dark:bg-dark-soft-blue"
                    : "border-[#e2ebef] bg-white hover:border-[#c9dce4] hover:bg-[#f8fbfc] dark:border-dark-border dark:bg-dark-surface dark:hover:border-dark-primary/40 dark:hover:bg-dark-blue-bg",
                ].join(" ")}
              >
                <button
                  type="button"
                  onClick={() => onSelect(account.id)}
                  className="flex min-w-0 flex-1 items-center gap-2.5 text-left sm:gap-3"
                >
                  {/* Avatar */}
                  <div
                    className={[
                      "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[10px] font-bold transition-colors sm:h-9 sm:w-9 sm:text-xs",
                      selected
                        ? "bg-[#147db5] text-white shadow-[0_5px_14px_rgba(20,125,181,0.18)] dark:bg-dark-primary"
                        : "bg-[#edf3f6] text-[#607787] dark:bg-dark-card dark:text-dark-text-secondary",
                    ].join(" ")}
                  >
                    {(account.business_name || account.name || "W")
                      .charAt(0)
                      .toUpperCase()}
                  </div>

                  {/* Account Details */}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[10px] font-semibold text-text-primary sm:text-xs dark:text-dark-text-primary">
                      {account.business_name ||
                        account.name ||
                        "WhatsApp Business"}
                    </p>

                    <p className="mt-0.5 truncate text-[8px] text-text-muted sm:text-[10px] dark:text-dark-text-muted">
                      {account.waba_id
                        ? `WABA ${account.waba_id}`
                        : account.business_id
                          ? `Business ${account.business_id}`
                          : "Business account"}
                    </p>
                  </div>
                </button>

                {/* Edit */}
                {onEdit && (
                  <button
                    type="button"
                    onClick={() => onEdit(account)}
                    aria-label="Edit account details"
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-text-muted transition-all hover:bg-theme-100 hover:text-primary dark:text-dark-text-muted dark:hover:bg-dark-card dark:hover:text-dark-primary"
                  >
                    <Pencil size={13} strokeWidth={1.8} />
                  </button>
                )}

                {/* Status Icon */}
                <div className="flex shrink-0 items-center justify-center">
                  {selected ? (
                    <Check
                      size={14}
                      strokeWidth={2}
                      className="text-[#147db5] sm:h-[15px] sm:w-[15px] dark:text-dark-primary"
                    />
                  ) : (
                    <ChevronRight
                      size={13}
                      strokeWidth={1.8}
                      className="text-text-muted/60 sm:h-[14px] sm:w-[14px] dark:text-dark-text-muted/60"
                    />
                  )}
                </div>
              </div>
            );
          })}

          {/* Add Another */}
          {onAdd && (
            <button
              type="button"
              onClick={onAdd}
              disabled={!workspaceId || !onAdd}
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-[#c9dce4] px-3 py-2.5 text-[9px] font-semibold text-[#607787] transition-all hover:border-[#9bcbe2] hover:bg-[#edf8fd] hover:text-[#147db5] disabled:cursor-not-allowed disabled:opacity-40 sm:gap-2 sm:text-[10px] dark:border-dark-border dark:text-dark-text-secondary dark:hover:border-dark-primary/45 dark:hover:bg-dark-soft-blue dark:hover:text-dark-primary"
            >
              <Plus size={12} strokeWidth={1.8} />
              Connect another account
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default WhatsAppAccountSection;
